import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { urlFor } from '../lib/sanity/client';
import { Link } from 'react-router-dom';
import React, { useState } from 'react';

interface Article {
  _id: string;
  title: string;
  slug: { current: string };
  excerpt: string;
  coverImage?: any;
  author?: string;
}

interface ArticleGridProps {
  articles: Article[];
  showAll?: boolean;
  hideHeading?: boolean;
}

export default function ArticleGrid({ articles, showAll = false, hideHeading = false }: ArticleGridProps) {
  
  const [error] = useState<string | null>(null);

  // Add error handling for articles
  if (!articles || articles.length === 0) {
    return (
      <div className="min-h-[50vh] flex items-center justify-center px-4">
        <p className="text-gray-600 text-center">
          {error || "No articles found. Please check back later."}
        </p>
      </div>
    );
  }
  const safeArticles = articles.filter(
    (article) => article && article._id && article.title && article.slug?.current
  );


  return (
    <section
      id="articles"
      className="py-12 md:py-20 px-4 bg-gradient-to-b from-blue-50 via-white to-blue-100 relative overflow-hidden"
      style={{backgroundImage: 'repeating-linear-gradient(0deg,rgba(0,0,0,0.04),rgba(0,0,0,0.04) 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,rgba(0,0,0,0.04),rgba(0,0,0,0.04) 1px,transparent 1px,transparent 40px)'}}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
    >
      <div className="max-w-7xl mx-auto">
        {!hideHeading && (
          <h2 className="text-3xl md:text-5xl font-serif text-center mb-8 md:mb-16 bg-gradient-to-r from-blue-900 to-blue-700 bg-clip-text text-transparent drop-shadow-lg">
            Latest Insights
          </h2>
        )}
        
        <div className="grid grid-cols-1 gap-8 md:gap-16">
          {safeArticles.map((article, index) => (
            <div
              key={article._id}
              className={`group flex flex-col md:flex-row w-full mb-8 transition-transform duration-300 ease-out hover:scale-[1.025] hover:shadow-2xl ${index % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
              style={{ minHeight: '280px' }}
            >
              {/* Image */}
              <div className="w-full md:w-1/3 h-48 md:h-auto flex-shrink-0 overflow-hidden rounded-xl">
                {article.coverImage && article.coverImage.asset ? (
                  <img
                    src={urlFor(article.coverImage).width(800).height(600).url()}
                    alt={article.title}
                    className="w-full h-full object-cover transition duration-300 group-hover:brightness-110 group-hover:scale-105"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full bg-blue-100 flex items-center justify-center">
                    <span className="text-blue-900">No image available</span>
                  </div>
                )}
              </div>
              {/* Content */}
              <div className="w-full md:w-2/3 flex items-center p-4 md:p-8">
                <div>
                  <h3 className="font-serif text-xl md:text-3xl mb-2 md:mb-3 text-blue-900 font-semibold drop-shadow-sm transition-colors duration-300 group-hover:text-blue-700">{article.title}</h3>
                  <p className="text-sm md:text-lg text-gray-700 mb-4 md:mb-5 leading-relaxed">{article.excerpt}</p>
                  <Link
                    to={`/articles/${article.slug.current}`}
                    className="inline-flex items-center text-blue-900 font-semibold transition-colors duration-300 hover:text-blue-700 group-hover:underline"
                  >
                    Read Analysis
                    <ArrowRight className="ml-2 w-4 h-4 md:w-5 md:h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
          {!showAll && articles.length > 0 && (
            <div className="flex justify-center mt-8 md:mt-12">
              <Link
                to="/articles"
                className="inline-block px-6 md:px-8 py-3 md:py-4 text-base md:text-lg font-bold text-blue-900 border-2 border-blue-900 rounded-full bg-white hover:bg-blue-900 hover:text-white transition-colors shadow-lg"
              >
                See All Articles
              </Link>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
